import React, { useContext, useEffect, useRef, useMemo } from "react";
import styled, { css } from "styled-components";
import fieldTypeFor from "views/logic/fieldtypes/FieldTypeFor";
import Value from "views/components/Value";
import DecoratedValue from "views/components/messagelist/decoration/DecoratedValue";
import CustomHighlighting from "views/components/highlighting/CustomHighlighting";
import RenderCompletionCallback from "views/components/widgets/RenderCompletionCallback";
import NumberVisualizationConfig from "views/logic/aggregationbuilder/visualizations/NumberVisualizationConfig";
import { makeVisualization, retrieveChartData } from "views/components/aggregationbuilder/AggregationBuilder";
import ElementDimensions from "components/common/ElementDimensions";
import useWidgetUnits from "views/components/visualizations/hooks/useWidgetUnits";
import useFeature from "hooks/useFeature";
import { UNIT_FEATURE_FLAG } from "views/components/visualizations/Constants";
import { parseSeries } from "views/logic/aggregationbuilder/Series";
import AutoFontSizer from "./AutoFontSizer";
import Trend from "./Trend";
import trendDirection, { trendBackground } from "./trendDirection";
const Container = styled.div(({ theme, $height, $trend }) => {
  const bgColor = trendBackground(theme, $trend);
  return css`
    height: ${$height}px;
    width: 100%;
    ${$trend && css`
      background-color: ${bgColor} !important; /* Needed for report generation */
      color: ${theme.utils.contrastingColor(bgColor)} !important; /* Needed for report generation */
      color-adjust: exact !important; /* Needed for report generation */
    `}
  `;
});
const GridContainer = styled(Container)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  gap: 0;
`;
const SingleItemGrid = styled(Container)`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: 0;
`;
const NumberBox = styled(ElementDimensions)`
  height: 100%;
  width: 100%;
  padding-bottom: ${({ theme }) => theme.spacings.xxs};
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;
const TrendBox = styled(ElementDimensions)`
  height: 100%;
  width: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;
const _extractValueAndField = (rows) => {
  if (!rows || !rows[0]) {
    return { value: void 0, field: void 0 };
  }
  const results = rows[0];
  if (results.source === "leaf") {
    const leaf = results.values.find((f) => f.source === "row-leaf");
    if (leaf && leaf.source === "row-leaf") {
      return { value: leaf.value, field: leaf.key[0] };
    }
  }
  return { value: void 0, field: void 0 };
};
const _extractFirstSeriesName = (config) => {
  const { series = [] } = config;
  return series.length === 0 ? void 0 : series[0].function;
};
const DEFAULT_ALIGNMENT = "bottom-right";
const NumberVisualization = ({ config, fields, data, height: heightProp }) => {
  var _a, _b;
  const targetRef = useRef();
  const unitFeatureEnabled = useFeature(UNIT_FEATURE_FLAG);
  const widgetUnits = useWidgetUnits(config);
  const onRenderComplete = useContext(RenderCompletionCallback);
  const visualizationConfig = (_a = config.visualizationConfig) != null ? _a : NumberVisualizationConfig.create();
  const field = _extractFirstSeriesName(config);
  const alignment = (_b = visualizationConfig.alignment) != null ? _b : DEFAULT_ALIGNMENT;
  useEffect(onRenderComplete, [onRenderComplete]);
  const chartRows = retrieveChartData(data);
  const trendRows = data.trend;
  const { value } = _extractValueAndField(chartRows);
  const { value: previousValue } = _extractValueAndField(trendRows || []);
  const unit = useMemo(() => {
    if (!unitFeatureEnabled) return void 0;
    const fieldNameKey = parseSeries(field).field;
    return widgetUnits.getFieldUnit(fieldNameKey);
  }, [field, unitFeatureEnabled, widgetUnits]);
  if (!field || value !== 0 && !value) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null, "N/A");
  }
  const ContainerComponent = visualizationConfig.trend ? GridContainer : SingleItemGrid;
  const trend = visualizationConfig.trend ? trendDirection(value, previousValue, visualizationConfig.trendPreference) : void 0;
  const highlightEntireWidget = visualizationConfig.colorPreference !== "TREND_ONLY";
  return /* @__PURE__ */ React.createElement(
    ContainerComponent,
    {
      $height: heightProp,
      $trend: highlightEntireWidget ? trend : void 0,
      "data-testid": "trend-background"
    },
    /* @__PURE__ */ React.createElement(NumberBox, { resizeDelay: 50 }, ({ height, width }) => /* @__PURE__ */ React.createElement(AutoFontSizer, { height, width, alignment }, /* @__PURE__ */ React.createElement(CustomHighlighting, { field, value }, /* @__PURE__ */ React.createElement(
      Value,
      {
        field,
        type: fieldTypeFor(field, fields),
        value,
        render: DecoratedValue,
        unit
      }
    )))),
    visualizationConfig.trend && /* @__PURE__ */ React.createElement(TrendBox, null, ({ height, width }) => /* @__PURE__ */ React.createElement(AutoFontSizer, { height, width, target: targetRef, alignment }, /* @__PURE__ */ React.createElement(
      Trend,
      {
        ref: targetRef,
        current: value,
        previous: previousValue,
        unit,
        trend: highlightEntireWidget ? void 0 : trend
      }
    )))
  );
};
const ConnectedNumberVisualization = makeVisualization(NumberVisualization, "numeric");
export default ConnectedNumberVisualization;
